import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Center, Icon, Spinner } from '@barev/ui';
import { errorText, haptic, useI18n, useToast, useTRPC } from '@barev/web';
import { useEffect, useState } from 'react';
import { JobRow, useJobSummary } from '../lib.tsx';

/** PRO-1, PRO-2, PRO-5: шапка, предложения, «Сейчас», заказы на сегодня */
export function Today() {
  const { t, amd, dt } = useI18n();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const summary = useJobSummary();
  const today = useQuery({ ...trpc.pro.today.queryOptions(), refetchInterval: 15_000 });
  const shift = useMutation(trpc.pro.setShift.mutationOptions({ onSuccess: () => qc.invalidateQueries() }));
  const respond = useMutation(
    trpc.pro.respondOffer.mutationOptions({
      onSuccess: (_, v) => {
        haptic.ok();
        toast(t(v.accept ? 'pro.offer.accepted' : 'pro.offer.declined'));
        void qc.invalidateQueries();
      },
      onError: (e) => {
        toast(errorText(t, e), 'error');
        void qc.invalidateQueries();
      },
    }),
  );

  if (!today.data)
    return (
      <Center>
        <Spinner />
      </Center>
    );
  const d = today.data;
  const current = d.orders.find((o) => o.id === d.currentOrderId);

  return (
    <main className="app">
      <Card tone="dark" className="bc-stack" style={{ padding: 20 }}>
        <div className="bc-between" style={{ alignItems: 'flex-start' }}>
          <div>
            <div className="bc-h2">{d.provider.name}</div>
            <div className="bc-small" style={{ color: '#CFC8C2' }}>
              {t(`roles.${d.provider.role}`)}
              {d.provider.companyName ? ` · ${d.provider.companyName}` : ''}
            </div>
          </div>
          <button className="toggle" aria-pressed={d.provider.onShift} onClick={() => shift.mutate({ onShift: !d.provider.onShift })} disabled={shift.isPending}>
            <span className="toggle__knob" />
            {t(d.provider.onShift ? 'pro.today.onShift' : 'pro.today.offShift')}
          </button>
        </div>
        <div className="stats">
          <div className="stat">
            <div className="bc-small" style={{ color: '#CFC8C2' }}>
              {t('pro.today.earned')}
            </div>
            <div className="stat__value">{amd(d.earningsToday)}</div>
          </div>
          <div className="stat">
            <div className="bc-small" style={{ color: '#CFC8C2' }}>
              {t('pro.today.orders')}
            </div>
            <div className="stat__value">{d.orders.length}</div>
          </div>
          <div className="stat">
            <div className="bc-small" style={{ color: '#CFC8C2' }}>
              {t('pro.today.rating')}
            </div>
            <div className="stat__value">{d.provider.rating ?? '—'}</div>
          </div>
        </div>
      </Card>

      {d.offers.map((f) => (
        <Card key={f.offerId} tone="pink" className="bc-stack">
          <div className="bc-between">
            <h2 className="bc-h3">{t('pro.offer.title')}</h2>
            {f.expiresAt && <Countdown until={f.expiresAt} />}
          </div>
          <div style={{ fontWeight: 600 }}>
            #{f.order.number} · {summary(f.order)}
          </div>
          <div className="bc-small">
            {dt(f.order.scheduledAt, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} ·{' '}
            {f.order.districtCode ? t(`districts.${f.order.districtCode}`) : t('common.online')} · {f.order.clientLocale.toUpperCase()} · {amd(f.order.totalPrice)}
          </div>
          <div className="bc-row">
            <Button style={{ flex: 1 }} onClick={() => respond.mutate({ offerId: f.offerId, accept: true })} disabled={respond.isPending}>
              {t('pro.offer.accept')}
            </Button>
            <Button variant="light" style={{ flex: 1 }} onClick={() => respond.mutate({ offerId: f.offerId, accept: false })} disabled={respond.isPending}>
              {t('pro.offer.decline')}
            </Button>
          </div>
        </Card>
      ))}

      {current && (
        <a href={`#/orders/${current.id}`} className="bc-card bc-tone-home" style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="bc-between">
            <span className="bc-badge">{t('pro.today.now')}</span>
            <span className="bc-badge">{t(`status.${current.flow}.${current.status}`)}</span>
          </div>
          <div className="bc-h2">
            {dt(current.scheduledAt, { hour: '2-digit', minute: '2-digit' })} · {summary(current)}
          </div>
          <div className="bc-row">
            <Icon name="pin" size={18} />
            {current.isOnline ? t('common.online') : `${current.districtCode ? t(`districts.${current.districtCode}`) + ', ' : ''}${current.addressText ?? ''}`}
          </div>
          <div className="bc-row bc-small">
            <Icon name="globe" size={16} />
            {t('pro.job.clientLanguage')}: {current.clientLocale.toUpperCase()}
            {current.clientName ? ` · ${current.clientName}` : ''}
          </div>
          {current.note && <div className="bc-small">💬 {current.note}</div>}
          <div className="bc-row" style={{ fontWeight: 700 }}>
            {t('pro.today.open')} <Icon name="arrow" size={18} />
          </div>
        </a>
      )}

      <section className="bc-stack" style={{ gap: 8 }}>
        <h2 className="bc-h3">{t('pro.today.title')}</h2>
        {d.orders.length === 0 ? (
          <p className="bc-muted" style={{ margin: 0 }}>
            {t('pro.today.empty')}
          </p>
        ) : (
          d.orders.map((o) => <JobRow key={o.id} j={o} href={`#/orders/${o.id}`} />)
        )}
      </section>

      {d.upcoming.length > 0 && (
        <section className="bc-stack" style={{ gap: 8 }}>
          <h2 className="bc-h3">{t('pro.today.upcoming')}</h2>
          {d.upcoming.map((o) => (
            <div key={o.id}>
              <div className="bc-small bc-muted" style={{ margin: '4px 4px' }}>
                {dt(o.scheduledAt, { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
              <JobRow j={o} href={`#/orders/${o.id}`} />
            </div>
          ))}
        </section>
      )}
    </main>
  );
}

function Countdown({ until }: { until: Date }) {
  const { t } = useI18n();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const left = Math.max(0, Math.floor((until.getTime() - now) / 1000));
  return (
    <span className="bc-badge">
      {t('pro.offer.left')} {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}
    </span>
  );
}
